import { APP_CONFIG } from '@config/index';
import { faCloudUpload, faTrash } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useCurrency } from '@modules/Orders/shared/hooks/useCurrency';
import { Button } from '@shared/components/ui/button';
import { DatePicker } from '@shared/components/ui/date-picker';
import { Input } from '@shared/components/ui/input';
import { ModalCenter, ModalCenterRef } from '@shared/components/ui/ModalCenter';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@shared/components/ui/select';
import { TextArea } from '@shared/components/ui/textarea';
import { useToast } from '@shared/hooks/use-toast';
import { getErrorMessage } from '@shared/utils/error.utils';
import { ArrowLeft, ArrowRight } from 'lucide-react';
import { forwardRef, useImperativeHandle, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useTopupsMutations } from '../../hooks/useTopupsMutations';

const PAYMENT_METHOD = 'Transferencia bancaria';
const SHOW_BANK_STEP = APP_CONFIG.warehouseCountry === 'ES';

type CreateTopupStep = 'bank' | 'form';

const getInitialStep = (): CreateTopupStep => (SHOW_BANK_STEP ? 'bank' : 'form');

const BANK_DETAIL_KEYS = ['beneficiary', 'iban', 'bic', 'beneficiaryAddress', 'bank', 'bankAddress'] as const;

interface BankDetailRowProps {
  label: string;
  value: string;
}

const BankDetailRow = ({ label, value }: BankDetailRowProps) => (
  <div className="space-y-0.5">
    <p className="text-sm font-medium text-foreground">{label}</p>
    <p className="text-sm text-muted-foreground break-words">{value}</p>
  </div>
);

export interface CreateTopupModalRef {
  open: (walletId: number) => void;
  close: () => void;
}

interface CreateTopupModalProps {
  onSuccess?: () => void;
}

export const CreateTopupModal = forwardRef<CreateTopupModalRef, CreateTopupModalProps>(({ onSuccess }, ref) => {
  const { t } = useTranslation('topupsModule');
  const { toast } = useToast();
  const currency = useCurrency();
  const modalRef = useRef<ModalCenterRef>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { createTopup } = useTopupsMutations();

  const [step, setStep] = useState<CreateTopupStep>(getInitialStep);
  const [walletId, setWalletId] = useState<number | null>(null);
  const [declaredAmount, setDeclaredAmount] = useState('');
  const [paymentMethod, setPaymentMethod] = useState(PAYMENT_METHOD);
  const [paymentReference, setPaymentReference] = useState('');
  const [transferDate, setTransferDate] = useState<Date | undefined>(undefined);
  const [payerName, setPayerName] = useState('');
  const [payerIban, setPayerIban] = useState('');
  const [internalNote, setInternalNote] = useState('');
  const [proofDocument, setProofDocument] = useState<File | null>(null);

  const resetForm = () => {
    setStep(getInitialStep());
    setDeclaredAmount('');
    setPaymentMethod(PAYMENT_METHOD);
    setPaymentReference('');
    setTransferDate(undefined);
    setPayerName('');
    setPayerIban('');
    setInternalNote('');
    setProofDocument(null);
  };

  useImperativeHandle(ref, () => ({
    open: (wallet) => {
      resetForm();
      setWalletId(wallet);
      modalRef.current?.openModal();
    },
    close: () => modalRef.current?.closeModal(),
  }));

  const handleClose = () => {
    resetForm();
    modalRef.current?.closeModal();
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      if (file.size > 2 * 1024 * 1024) {
        toast({
          title: t('errors.fileTooLarge'),
          description: t('errors.fileTooLargeDescription'),
          variant: 'destructive',
        });
        return;
      }
      const validTypes = ['application/pdf', 'image/jpeg', 'image/png'];
      if (!validTypes.includes(file.type)) {
        toast({
          title: t('errors.invalidFileType'),
          description: t('errors.invalidFileTypeDescription'),
          variant: 'destructive',
        });
        return;
      }
      setProofDocument(file);
    }
  };

  const handleRemoveFile = () => {
    setProofDocument(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleSubmit = async () => {
    if (!walletId) return;
    try {
      await createTopup.mutateAsync({
        walletId,
        payload: {
          declared_amount: Number(declaredAmount),
          payment_method: paymentMethod.trim(),
          payment_reference: paymentReference.trim(),
          transfer_date: transferDate ? transferDate.toISOString().split('T')[0] : '',
          payer_name: payerName.trim(),
          payer_iban: payerIban.replace(/\s/g, ''),
          proof_document_file: proofDocument || undefined,
          internal_note: internalNote.trim(),
        },
      });

      toast({
        title: t('modals.create.success.title'),
        description: t('modals.create.success.description'),
      });
      resetForm();
      modalRef.current?.closeModal();
      onSuccess?.();
    } catch (error) {
      toast({
        title: t('errors.createFailed'),
        description: getErrorMessage(error),
        variant: 'destructive',
      });
    }
  };

  const isLoading = createTopup.isPending;
  const isFormValid =
    walletId &&
    declaredAmount.trim() &&
    Number(declaredAmount) >= 0.01 &&
    paymentMethod === PAYMENT_METHOD &&
    paymentReference.trim() &&
    transferDate !== undefined &&
    payerName.trim() &&
    payerIban.trim() &&
    internalNote.trim() &&
    proofDocument !== null;

  const renderBankStep = () => (
    <>
      <div className="space-y-4">
        <div className="space-y-1">
          <h3 className="text-base font-semibold text-foreground">{t('modals.create.bankTransfer.title')}</h3>
          <p className="text-sm text-muted-foreground">{t('modals.create.bankTransfer.description')}</p>
        </div>

        <div className="rounded-lg border border-border bg-muted/30 p-4 space-y-4">
          {BANK_DETAIL_KEYS.map((key) => (
            <BankDetailRow
              key={key}
              label={t(`modals.create.bankTransfer.${key}.label`)}
              value={t(`modals.create.bankTransfer.${key}.value`)}
            />
          ))}
        </div>
      </div>

      <div className="flex justify-end gap-2 pt-4">
        <Button variant="outline" onClick={handleClose}>
          {t('buttons.cancel')}
        </Button>
        <Button variant="primary" onClick={() => setStep('form')} data-testid="create-topup-next-button">
          {t('buttons.next')}
          <ArrowRight className="ml-1.5 h-4 w-4" />
        </Button>
      </div>
    </>
  );

  const renderFormStep = () => (
    <>
      <div className="space-y-4">
        <p className="text-sm text-muted-foreground">{t('modals.create.description')}</p>

        <div className="space-y-1.5">
          <label className="text-sm font-medium text-foreground">
            {t('modals.create.fields.declared_amount')} ({currency}) *
          </label>
          <Input
            type="number"
            step="0.01"
            min="0.01"
            value={declaredAmount}
            onChange={(e) => setDeclaredAmount(e.target.value)}
            disabled={isLoading}
            placeholder="100.00"
          />
        </div>

        <div className="space-y-1.5">
          <label className="text-sm font-medium text-foreground">{t('modals.create.fields.payment_method')} *</label>
          <Select value={paymentMethod} onValueChange={setPaymentMethod} disabled={isLoading}>
            <SelectTrigger className="w-full">
              <SelectValue placeholder={t('modals.create.fields.payment_method')} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={PAYMENT_METHOD}>{t('modals.create.fields.payment_method_bank_transfer')}</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-1.5">
          <label className="text-sm font-medium text-foreground">{t('modals.create.fields.payment_reference')} *</label>
          <Input
            value={paymentReference}
            onChange={(e) => setPaymentReference(e.target.value)}
            disabled={isLoading}
            placeholder={t('modals.create.fields.payment_reference_placeholder')}
            maxLength={255}
          />
        </div>

        <div className="space-y-1.5">
          <label className="text-sm font-medium text-foreground">{t('modals.create.fields.transfer_date')} *</label>
          <DatePicker
            date={transferDate}
            onDateChange={setTransferDate}
            placeholder={t('modals.create.fields.transfer_date')}
            disabled={isLoading}
          />
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="space-y-1.5">
            <label className="text-sm font-medium text-foreground">{t('modals.create.fields.payer_name')} *</label>
            <Input
              value={payerName}
              onChange={(e) => setPayerName(e.target.value)}
              disabled={isLoading}
              maxLength={255}
            />
          </div>
          <div className="space-y-1.5">
            <label className="text-sm font-medium text-foreground">{t('modals.create.fields.payer_iban')} *</label>
            <Input
              value={payerIban}
              onChange={(e) => setPayerIban(e.target.value.toUpperCase())}
              disabled={isLoading}
              maxLength={34}
            />
          </div>
        </div>

        <div className="space-y-1.5">
          <label className="text-sm font-medium text-foreground">
            {t('modals.create.fields.proof_document_file')} *
          </label>
          {!proofDocument ? (
            <div className="flex items-center gap-2">
              <input
                ref={fileInputRef}
                type="file"
                accept=".pdf,.jpg,.jpeg,.png"
                onChange={handleFileChange}
                disabled={isLoading}
                className="hidden"
                id="proof-document-input"
              />
              <label
                htmlFor="proof-document-input"
                className="flex items-center gap-2 px-4 py-2 border border-dashed rounded-md cursor-pointer hover:bg-accent transition-colors"
              >
                <FontAwesomeIcon icon={faCloudUpload} className="w-4 h-4" />
                <span className="text-sm">{t('modals.create.fields.upload_file')}</span>
              </label>
            </div>
          ) : (
            <div className="flex items-center justify-between p-3 bg-muted rounded-md">
              <span className="text-sm truncate">{proofDocument.name}</span>
              <Button type="button" variant="ghost" size="sm" onClick={handleRemoveFile} disabled={isLoading}>
                <FontAwesomeIcon icon={faTrash} className="w-4 h-4 text-destructive" />
              </Button>
            </div>
          )}
          <p className="text-xs text-muted-foreground">{t('modals.create.fields.proof_document_hint')}</p>
        </div>

        <div className="space-y-1.5">
          <label className="text-sm font-medium text-foreground">{t('modals.create.fields.internal_note')} *</label>
          <TextArea
            value={internalNote}
            onChange={(e) => setInternalNote(e.target.value)}
            disabled={isLoading}
            rows={3}
            placeholder={t('modals.create.fields.internal_note_placeholder')}
            maxLength={1000}
          />
        </div>
      </div>

      <div className={SHOW_BANK_STEP ? 'flex items-center justify-between gap-2 pt-4' : 'flex justify-end gap-2 pt-4'}>
        {SHOW_BANK_STEP && (
          <Button
            variant="outline"
            onClick={() => setStep('bank')}
            disabled={isLoading}
            data-testid="create-topup-back-button"
          >
            <ArrowLeft className="mr-1.5 h-4 w-4" />
            {t('buttons.back')}
          </Button>
        )}
        <div className="flex gap-2">
          <Button variant="outline" onClick={handleClose} disabled={isLoading}>
            {t('buttons.cancel')}
          </Button>
          <Button variant="primary" onClick={handleSubmit} disabled={!isFormValid || isLoading}>
            {isLoading ? t('buttons.creating') : t('buttons.confirm')}
          </Button>
        </div>
      </div>
    </>
  );

  return (
    <ModalCenter
      ref={modalRef}
      title={t('modals.create.title')}
      type="default"
      width="lg"
      className="max-h-[90vh] overflow-y-auto"
      onCloseModal={resetForm}
    >
      {SHOW_BANK_STEP && step === 'bank' ? renderBankStep() : renderFormStep()}
    </ModalCenter>
  );
});

CreateTopupModal.displayName = 'CreateTopupModal';
